/**
 * CR140: Scheduler List Component
 * CR140.1: Enhanced Last Run display with counts and error tooltips
 * CR140.2: Timezone clarity - show (ET) suffix and UTC debug tooltips
 * CR156: Added "Run until" pill with tooltip
 */

import { 
  Calendar, 
  Clock, 
  Play, 
  Pause, 
  Trash2, 
  Edit, 
  History, 
  Zap,
  Plus,
  RefreshCw,
  AlertCircle,
  Square,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Skeleton } from "@/components/ui/skeleton";
import type { ScheduleWithMerchants, ScheduleRunStatus, BatchRunScheduleRun } from "@/lib/scheduler/types";
import { 
  formatScheduleTime, 
  getUtcDebugString,
  getTimezoneLabel 
} from "@/lib/scheduler/timezoneUtils";
import { getRunUntilConfig } from "@/lib/scheduler/runUntilConfig";

interface SchedulerListProps {
  schedules: ScheduleWithMerchants[];
  loading: boolean;
  onEdit: (schedule: ScheduleWithMerchants) => void;
  onDelete: (id: string) => Promise<boolean>;
  onToggleEnabled: (id: string, enabled: boolean) => Promise<boolean>;
  onRunNow: (id: string) => Promise<boolean>;
  onStopRun: (id: string) => Promise<boolean>;
  onViewHistory: (schedule: ScheduleWithMerchants) => void;
  onRefresh: () => void;
  onCreateNew: () => void;
}

function getStatusBadge(status: ScheduleRunStatus | undefined) {
  if (!status) return null;
  
  const variants: Record<ScheduleRunStatus, { variant: "default" | "secondary" | "destructive" | "outline"; label: string }> = {
    PENDING: { variant: "outline", label: "Pending" },
    DISPATCHED: { variant: "secondary", label: "Running" },
    SUCCESS: { variant: "default", label: "Success" },
    ERROR: { variant: "destructive", label: "Error" },
    CANCELLED: { variant: "outline", label: "Cancelled" },
  };
  
  const config = variants[status];
  return <Badge variant={config.variant}>{config.label}</Badge>;
}

function getSourceBadges(sources: string[]) {
  return sources.map(source => (
    <Badge key={source} variant="outline" className="text-xs">
      {source}
    </Badge>
  ));
}

/**
 * CR140.2: Time cell with (ET) suffix and UTC debug tooltip
 */
function TimeWithTooltip({ utcTimestamp }: { utcTimestamp: string | null }) {
  if (!utcTimestamp) {
    return <span className="text-muted-foreground text-sm">—</span>;
  }

  const displayTime = formatScheduleTime(utcTimestamp, { includeTimezone: true });
  const utcDebug = getUtcDebugString(utcTimestamp);

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div className="flex items-center gap-1 text-sm cursor-help">
          <Clock className="h-3 w-3" />
          <span>{displayTime}</span>
        </div>
      </TooltipTrigger>
      <TooltipContent side="top" className="text-xs">
        <p>{utcDebug}</p>
        <p className="text-muted-foreground mt-1">Timezone: {getTimezoneLabel()}</p>
      </TooltipContent>
    </Tooltip>
  );
}

/**
 * CR140.1: Enhanced Last Run cell with counts and error tooltip
 * CR140.2: Added (ET) suffix and UTC debug tooltip
 */
function LastRunCell({ run }: { run: BatchRunScheduleRun }) {
  const hasError = run.status === "ERROR";
  const displayTime = formatScheduleTime(run.scheduled_for, { includeTimezone: true });
  const utcDebug = getUtcDebugString(run.scheduled_for);
  const countDisplay = `${run.created_run_count}/${run.expected_run_count}`;
  
  return (
    <TooltipProvider>
      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-1.5">
          {getStatusBadge(run.status)}
          {hasError && run.error_message && (
            <Tooltip>
              <TooltipTrigger asChild>
                <AlertCircle className="h-3.5 w-3.5 text-destructive cursor-help" />
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-xs">
                <p className="text-xs break-words">{run.error_message}</p>
              </TooltipContent>
            </Tooltip>
          )}
        </div>
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="flex items-center gap-2 text-xs text-muted-foreground cursor-help">
              <span>{displayTime}</span>
              <span className="font-medium">{countDisplay}</span>
            </div>
          </TooltipTrigger>
          <TooltipContent side="top" className="text-xs">
            <p>{utcDebug}</p>
          </TooltipContent>
        </Tooltip>
      </div>
    </TooltipProvider>
  );
}

export function SchedulerList({
  schedules,
  loading,
  onEdit,
  onDelete,
  onToggleEnabled,
  onRunNow,
  onStopRun,
  onViewHistory,
  onRefresh,
  onCreateNew,
}: SchedulerListProps) {
  if (loading) {
    return (
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-9 w-32" />
        </div>
        <div className="border rounded-lg">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Merchants</TableHead>
                <TableHead>Sources</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Run At</TableHead>
                <TableHead>Last Run</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {[1, 2, 3].map(i => (
                <TableRow key={i}>
                  <TableCell><Skeleton className="h-5 w-32" /></TableCell>
                  <TableCell><Skeleton className="h-5 w-16" /></TableCell>
                  <TableCell><Skeleton className="h-5 w-24" /></TableCell>
                  <TableCell><Skeleton className="h-5 w-20" /></TableCell>
                  <TableCell><Skeleton className="h-5 w-28" /></TableCell>
                  <TableCell><Skeleton className="h-5 w-20" /></TableCell>
                  <TableCell><Skeleton className="h-8 w-24 ml-auto" /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-2">
          <h2 className="text-lg font-semibold">Schedules</h2>
          <Badge variant="secondary">{schedules.length}</Badge>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={onRefresh}>
            <RefreshCw className="h-4 w-4 mr-1" />
            Refresh
          </Button>
          <Button size="sm" onClick={onCreateNew}>
            <Plus className="h-4 w-4 mr-1" />
            Create Schedule
          </Button>
        </div>
      </div>

      {schedules.length === 0 ? (
        <div className="border rounded-lg p-12 text-center">
          <Calendar className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium mb-2">No Schedules</h3>
          <p className="text-muted-foreground mb-4">
            Create your first schedule to automate batch runs.
          </p>
          <Button onClick={onCreateNew}>
            <Plus className="h-4 w-4 mr-1" />
            Create Schedule
          </Button>
        </div>
      ) : (
        <div className="border rounded-lg">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Merchants</TableHead>
                <TableHead>Sources</TableHead>
                <TableHead>Run Until</TableHead>
                <TableHead>Enabled</TableHead>
                <TableHead>Run At</TableHead>
                <TableHead>Last Run</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {schedules.map(schedule => {
                const runUntilConfig = getRunUntilConfig(schedule.approval_checkpoint);
                
                return (
                  <TableRow key={schedule.id}>
                    <TableCell className="font-medium">{schedule.name}</TableCell>
                    <TableCell>
                      <Badge variant="secondary">{schedule.merchant_count}</Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1 flex-wrap">
                        {getSourceBadges(schedule.sources_enabled)}
                      </div>
                    </TableCell>
                    <TableCell>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Badge variant="outline" className="cursor-help">
                              Run until: {runUntilConfig.label}
                            </Badge>
                          </TooltipTrigger>
                          <TooltipContent side="top" className="max-w-xs">
                            <p className="font-medium">{runUntilConfig.tooltipTitle}</p>
                            <p className="text-xs text-muted-foreground mt-1">
                              {runUntilConfig.tooltipBody}
                            </p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </TableCell>
                    <TableCell>
                      <Badge variant={schedule.is_enabled ? "default" : "outline"}>
                        {schedule.is_enabled ? "Enabled" : "Paused"}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <TimeWithTooltip utcTimestamp={schedule.run_at} />
                    </TableCell>
                    <TableCell>
                      {schedule.last_run ? (
                        <LastRunCell run={schedule.last_run} />
                      ) : (
                        <span className="text-muted-foreground text-sm">Never</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => onRunNow(schedule.id)}
                          title="Run Now"
                        >
                          <Zap className="h-4 w-4" />
                        </Button>
                        {/* Show Stop button when there's an active (non-terminal) run */}
                        {schedule.last_run && (schedule.last_run.status === 'PENDING' || schedule.last_run.status === 'DISPATCHED') ? (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => onStopRun(schedule.id)}
                            title="Stop Run"
                            className="text-destructive hover:text-destructive"
                          >
                            <Square className="h-4 w-4" />
                          </Button>
                        ) : (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => onToggleEnabled(schedule.id, !schedule.is_enabled)}
                            title={schedule.is_enabled ? "Pause Schedule" : "Resume Schedule"}
                          >
                            {schedule.is_enabled ? (
                              <Pause className="h-4 w-4" />
                            ) : (
                              <Play className="h-4 w-4" />
                            )}
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => onViewHistory(schedule)}
                          title="View History"
                        >
                          <History className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => onEdit(schedule)}
                          title="Edit"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="ghost" size="icon" title="Delete">
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete Schedule</AlertDialogTitle>
                              <AlertDialogDescription>
                                Are you sure you want to delete "{schedule.name}"? This action cannot be undone.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => onDelete(schedule.id)}
                                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                              >
                                Delete
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
